/**
 * Supplies the analysis views (What-If, Sensitivity) with the model on screen.
 *
 * Each workspace provides it - IAS accounting from its store, Baseline Accounting from its
 * own. There is no fallback: a view outside a provider is a mistake, and it says so.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type {
  AnnualSummaryComputed,
  ModelSettings,
  SensitivityComputed,
  ValuationComputed,
  WhatIfComputed,
} from '../../types/financial-model';

export interface AnalysisSource {
  settings: ModelSettings | undefined;
  annualSummary: AnnualSummaryComputed | null | undefined;
  whatIf: WhatIfComputed | null | undefined;
  sensitivity: SensitivityComputed | null | undefined;
  valuation: ValuationComputed | null | undefined;
  isRecalculating: boolean;
  isDirty: boolean;
  /** Change one setting. Both stores debounce and recompute behind this. */
  updateSetting: (key: string, value: unknown) => void;
  /** Force a save+recompute now, for controls that should not wait. */
  recalculate: () => void | Promise<unknown>;
}

const AnalysisSourceContext = createContext<AnalysisSource | null>(null);

export function AnalysisSourceProvider({
  value,
  children,
}: {
  value: AnalysisSource;
  children: ReactNode;
}) {
  return (
    <AnalysisSourceContext.Provider value={value}>
      {children}
    </AnalysisSourceContext.Provider>
  );
}

export function useAnalysisSource(): AnalysisSource {
  const provided = useContext(AnalysisSourceContext);
  if (!provided) throw new Error('useAnalysisSource needs an AnalysisSourceProvider');
  return provided;
}
